import { Market } from '../context/MarketContext';

export type ProductColor = 'primary' | 'accent';
export type ProductIcon = 'operations' | 'payroll' | 'pharmacy';

export interface ProductData {
  id: string;
  markets: Market[];
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  icon: ProductIcon;
  screenshot?: string;
  screenshotAlt?: string;
  features: { title: string; description: string }[];
  benefits: string[];
  useCases: string[];
  color: ProductColor;
  prices: Partial<Record<Market, string>>;
}

export const products: ProductData[] = [
  {
    id: 'cimmpleflow',
    markets: ['US', 'IN'],
    name: 'CimmpleFlow',
    tagline: 'Manufacturing & Logistics Made Simple',
    description: 'Complete Information on Manufacturing Parts, Planning and Logistics Evaluation. A cloud-based ERP solution that gives you full visibility and control over your business operations—anytime, anywhere.',
    longDescription: `CimmpleFlow is our flagship cloud-based ERP solution designed for manufacturing and logistics businesses of all sizes. It provides complete visibility and control over your entire business operations—anytime, anywhere.

From quotation management with VendorConnect™ to job tracking with live updates, CimmpleFlow streamlines your workflows and helps you make data-driven decisions. Our comprehensive suite of modules covers everything from purchasing and quality control to advanced accounting and document management.`,
    icon: 'operations',
    screenshot: '/images/platform/cimmpleflow-home.png',
    screenshotAlt: 'CimmpleFlow dashboard showing orders, jobs, revenue, and operational analytics',
    features: [
      { title: 'Quotations', description: 'Create, manage, and track quotations with VendorConnect™ for seamless supplier collaboration.' },
      { title: 'Order Management', description: 'Handle all your orders from creation to fulfillment with complete tracking and visibility.' },
      { title: 'Purchasing', description: 'Streamline your procurement process with automated purchase orders and supplier management.' },
      { title: 'Job Management', description: 'Track jobs in real-time with live updates, scheduling, and resource allocation.' },
      { title: 'Quality Control', description: 'Ensure product quality with live reporting, inspections, and compliance tracking.' },
      { title: 'Reporting & Analytics', description: 'Generate scheduled reports and gain insights with comprehensive business analytics.' },
      { title: 'Accounting', description: 'Advanced accounting features including invoicing, expenses, and financial reporting.' },
      { title: 'AI Invoice Capture', description: 'Upload a PDF or receive vendor emails with attached invoices and automatically extract all invoice data directly into the form.' },
      { title: 'Timesheets', description: 'Track employee time with third-party integrations for payroll and project costing.' },
      { title: 'Document Storage', description: 'Secure document management with version control and easy access to all your files.' },
    ],
    benefits: [
      'Cloud-based access from anywhere',
      'Real-time live tracking and updates',
      'Streamlined business workflows',
      'Complete operational visibility',
      'Reduced manual data entry',
      'AI-powered invoice extraction from PDFs and email',
      'Improved decision making',
    ],
    useCases: [
      'Manufacturing companies',
      'Engineering firms',
      'Construction businesses',
      'Logistics and distribution',
      'Job shops and workshops',
      'Service-based businesses',
    ],
    color: 'primary',
    prices: {
      US: 'From $69/user/month',
      IN: '₹4,000/month',
    },
  },
  {
    id: 'cimmlepay',
    markets: ['US'],
    name: 'CimmplePay',
    tagline: 'Payroll Processing, Simplified',
    description: 'An intelligent payroll management solution that automates salary processing, tax calculations, and compliance reporting, so you can focus on what matters most—your people.',
    longDescription: `CimmplePay is our upcoming payroll management solution designed to take the complexity out of paying your employees. Once launched, it will handle everything from salary calculations and tax withholdings to biometric attendance and year-end reporting.

With built-in tax tables that will be automatically updated, CimmplePay will ensure your payroll is always compliant with the latest regulations. The employee self-service portal will empower your team to access their information, reducing HR workload.`,
    icon: 'payroll',
    screenshot: '/images/platform/cimmplepay-home.png',
    screenshotAlt: 'CimmplePay payroll management dashboard',
    features: [
      { title: 'Automated Calculations', description: 'Calculate salaries, overtime, bonuses, and deductions automatically with precision.' },
      { title: 'Tax Compliance', description: 'Stay compliant with automatic tax calculations, withholdings, and filings.' },
      { title: 'Employee Self-Service', description: 'Let employees view pay stubs, update information, and access tax documents.' },
      { title: 'Biometric Face-Recognition Attendance', description: 'Clock in and out with secure face recognition for accurate, fraud-resistant attendance tracking.' },
      { title: 'Leave Management', description: 'Track vacation, sick leave, and PTO with automated accrual calculations.' },
      { title: 'Comprehensive Reporting', description: 'Generate detailed reports for payroll, taxes, labor costs, and compliance.' },
    ],
    benefits: [
      'Process payroll in minutes, not hours',
      'Achieve 100% tax compliance',
      'Eliminate manual calculation errors',
      'Ensure employees are paid on time',
      'Reduce HR administrative workload',
      'Access payroll data from anywhere',
    ],
    useCases: [
      'Small and medium businesses',
      'Growing startups',
      'Multi-state employers',
      'Companies with hourly workers',
      'Organizations with complex pay structures',
      'Businesses with remote teams',
    ],
    color: 'accent',
    prices: { US: '$29/month' },
  },
  {
    id: 'cimmplerx',
    markets: ['IN'],
    name: 'CimmpleRx',
    tagline: 'A Smarter ERP for Pharmacies',
    description: 'Manage pharmacy billing, batch-wise inventory, purchases, accounting, GST, customers, and vendors from one connected system.',
    longDescription: `CimmpleRx is a pharmacy ERP built for medical shops, wholesalers, and agencies in India. It brings billing, inventory, purchases, customer and vendor records, payments, and accounting together in one practical workspace.

Track medicines by batch and expiry date, stay ahead of low stock, manage GST-ready transactions, and understand daily sales at a glance. Role-based access and shop-level data isolation help every team work securely with the information they need.`,
    icon: 'pharmacy',
    features: [
      { title: 'Pharmacy Billing', description: 'Run point-of-sale billing, generate invoices, and accept cash, card, UPI, or credit payments.' },
      { title: 'Batch & Expiry Tracking', description: 'Track medicine batches, manufacturing details, expiry dates, and expiring stock.' },
      { title: 'Inventory Management', description: 'See stock in real time, set reorder points, and receive low-stock notifications.' },
      { title: 'Smart Ordering', description: 'Use sales velocity, stock levels, and vendor schedules to prepare suggested purchase orders.' },
      { title: 'Purchase Management', description: 'Create purchase orders and manage them from draft through confirmation and receipt.' },
      { title: 'GST & Accounting', description: 'Manage your chart of accounts, journal entries, GST calculations, and GST reports.' },
      { title: 'Customer & Vendor Management', description: 'Maintain customer and vendor records, GSTIN details, credit limits, and outstanding balances.' },
      { title: 'Reports & Analytics', description: 'Review sales, purchases, payment methods, inventory value, and operational trends.' },
      { title: 'Role-Based Access', description: 'Give admins, managers, accountants, and salespeople access appropriate to their responsibilities.' },
    ],
    benefits: [
      'One workspace for daily pharmacy operations',
      'Real-time stock and sales visibility',
      'Fewer losses from expired medicines',
      'Faster counter billing and collections',
      'Timely, data-informed purchase planning',
      'GST-ready financial records and reports',
      'Secure access for each team role',
    ],
    useCases: [
      'Retail pharmacies',
      'Medical shops',
      'Pharmacy chains',
      'Medicine wholesalers',
      'Pharmaceutical agencies',
      'Multi-branch operations',
    ],
    color: 'primary',
    prices: { IN: 'From ₹300/month' },
  },
];

export const getProductsForMarket = (market: Market): ProductData[] =>
  products.filter((product) => product.markets.includes(market));

export const getProductById = (productId: string): ProductData | undefined =>
  products.find((product) => product.id === productId);

export const getProductPrice = (product: ProductData, market: Market): string =>
  product.prices[market] ?? product.prices.US ?? '';
